import React, {useEffect, useState} from 'react';
import {ErrorMessage, Field, FieldArray, Form, Formik} from 'formik';
import {useNavigate, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {IoAdd, IoChevronDown, IoChevronUp, IoTrash} from 'react-icons/io5';
import "pages/grounds/_groundDetail.scss";
import "pages/grounds/_groundCreate.scss";
import {api} from 'api/calls';
import {routes} from 'common';
import {groundValidationSchema, DAYS_OF_WEEK, MAX_FILE_SIZE} from './constants';
import {ComLoader, Loader, MultiImages, MultiSelect, ConfirmModal, GoogleLocationPicker} from 'components';
import {GOOGLE_MAPS_API_KEY} from 'config';
import {generalExtraActions} from 'reduxStore/actions';
import {APPROVAL_STATUS, APPROVAL_STATUS_LABEL} from "../../common/constants";

function GroundDetail({approvalMode = false}) {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    sports,
    sportsLoading,
    sportsError,
    amenities,
    amenitiesLoading,
    amenitiesError
  } = useSelector((state) => state.general);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [groundData, setGroundData] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [expandedFields, setExpandedFields] = useState(new Set());
  const [expandedSlots, setExpandedSlots] = useState(new Set());
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [expandedLocation, setExpandedLocation] = useState(true);
  const [expandedGroundFields, setExpandedGroundFields] = useState(false);
  const [expandedBasicInfo, setExpandedBasicInfo] = useState(true);
  const [expandedImages, setExpandedImages] = useState(true);
  const [uploadError, setUploadError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [originalData, setOriginalData] = useState(null); // Store original data for comparison
  const [changes, setChanges] = useState({}); // Track changes
  // Approval status state
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null);

  useEffect(() => {
    dispatch(generalExtraActions.getSports());
    dispatch(generalExtraActions.getAmenities());
  }, [dispatch]);

  useEffect(() => {
    const fetchGroundData = async () => {
      setFetchLoading(true);
      try {
        const response = await api.getGroundDetail(id);
        setGroundData(response);
        setOriginalData(response); // Store original data for comparison
        setApprovalStatus(response.approval_status || APPROVAL_STATUS.PENDING);
      } catch (error) {
        console.error('Error fetching ground:', error);
        navigate(routes.GROUNDS);
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchGroundData();
    }
  }, [id, navigate]);

  const initialValues = {
    name: groundData?.name || '',
    contact_number: groundData?.contact_number || '',
    email: groundData?.email || '',
    description: groundData?.description || '',
    opening_time: groundData?.opening_time ? groundData.opening_time.slice(0, 5) : '',
    closing_time: groundData?.closing_time ? groundData.closing_time.slice(0, 5) : '',
    is_active: groundData?.is_active ?? true,
    amenities: groundData?.amenities?.map(amenity => amenity.id) || [],
    location: {
      city: groundData?.location?.city?.name || groundData?.location?.city || '',
      address: groundData?.location?.address || '',
      latitude: groundData?.location?.latitude || null,
      longitude: groundData?.location?.longitude || null,
      place_id: groundData?.location?.place_id || null,
    },
    ground_fields: groundData?.ground_fields?.map(field => ({
      ...field,
      sports: field.sports?.map(sport => sport.id) || [],
      images: field.images || [],
      field_slots: field.field_slots ? Object.keys(field.field_slots).reduce((acc, day) => {
        acc[day] = field.field_slots[day].map(slot => ({
          ...slot,
          start_time: slot.start_time ? slot.start_time.slice(0, 5) : '',
          end_time: slot.end_time ? slot.end_time.slice(0, 5) : '',
          price: slot.price || 1000
        }));
        return acc;
      }, {}) : DAYS_OF_WEEK.reduce((acc, day) => {
        acc[day] = [{start_time: '', end_time: '', price: 1000}];
        return acc;
      }, {})
    })) || [{
      name: '',
      sports: [],
      images: [],
      field_slots: DAYS_OF_WEEK.reduce((acc, day) => {
        acc[day] = [{start_time: '', end_time: '', price: 1000}];
        return acc;
      }, {})
    }],
    images: groundData?.images || [],
    images_to_delete: [],
  };

  const handleSubmit = async (values, {setSubmitting, setFieldError}) => {
    setLoading(true);
    try {
      // Validate required fields before submission
      const requiredFields = {
        name: values.name,
        contact_number: values.contact_number,
        email: values.email,
        description: values.description,
        opening_time: values.opening_time,
        closing_time: values.closing_time,
        amenities: values.amenities,
        location: values.location,
      };

      // Check if any required fields are empty
      const emptyFields = Object.entries(requiredFields).filter(([key, value]) => {
        if (key === 'location') {
          return !value.city || !value.address;
        }
        if (Array.isArray(value)) {
          return !value || value.length === 0;
        }
        return !value || value === '';
      });

      if (emptyFields.length > 0) {
        toast.error('Please fill in all required fields before submitting.');
        setLoading(false);
        setSubmitting(false);
        return;
      }

      // Track changes
      const trackedChanges = trackChanges(values);

      console.log(trackedChanges)

      // If no changes, show message and return
      if (Object.keys(trackedChanges).length === 0) {
        toast.info('No changes detected. Ground data is up to date.');
        setLoading(false);
        setSubmitting(false);
        return;
      }

      // Function to convert file to base64
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      };

      // Process changes and convert images to base64
      const processedChanges = {...trackedChanges};

      // Process main images if there are changes
      if (trackedChanges.images) {
        const imageChanges = {...trackedChanges.images};

        // Convert new images to base64
        if (imageChanges.added && imageChanges.added.length > 0) {
          const convertedImages = [];
          for (const image of imageChanges.added) {
            try {
              const base64 = await fileToBase64(image.image);
              convertedImages.push({
                image: base64,
                is_primary: image.is_primary
              });
            } catch (error) {
              console.error('Error converting image to base64:', error);
              toast.error(`Error processing image: ${image.image.name}`);
            }
          }
          imageChanges.added = convertedImages;
        }

        // Only send id and is_primary for modified images (no image property)
        if (imageChanges.modified && imageChanges.modified.length > 0) {
          imageChanges.modified = imageChanges.modified.map(img => ({
            id: img.id,
            is_primary: img.is_primary
          }));
        }

        processedChanges.images = imageChanges;
      }

      // Debug: Log what's being sent
      console.log('Changes being sent:', processedChanges);

      await api.updateGround(id, processedChanges);
      toast.success('Ground updated successfully!');

      // Update original data to reflect the changes
      setOriginalData(prev => ({...prev, ...processedChanges}));

      navigate(routes.GROUNDS);
    } catch (error) {
      console.error('Error updating ground:', error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.deleteGround(id);
      toast.success('Ground deleted successfully!');
      navigate(routes.GROUNDS);
    } catch (error) {
      console.error('Error deleting ground:', error);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleCancel = () => {
    navigate(routes.GROUNDS);
  };

  const toggleFieldExpansion = (fieldIndex) => {
    const newExpandedFields = new Set(expandedFields);
    if (newExpandedFields.has(fieldIndex)) {
      newExpandedFields.delete(fieldIndex);
    } else {
      newExpandedFields.add(fieldIndex);
    }
    setExpandedFields(newExpandedFields);
  };

  const toggleSlotsExpansion = (fieldIndex) => {
    const newExpandedSlots = new Set(expandedSlots);
    if (newExpandedSlots.has(fieldIndex)) {
      newExpandedSlots.delete(fieldIndex);
    } else {
      newExpandedSlots.add(fieldIndex);
    }
    setExpandedSlots(newExpandedSlots);
  };

  const toggleDayExpansion = (day) => {
    const newExpandedDays = new Set(expandedDays);
    if (newExpandedDays.has(day)) {
      newExpandedDays.delete(day);
    } else {
      newExpandedDays.add(day);
    }
    setExpandedDays(newExpandedDays);
  };

  const toggleLocationExpansion = () => {
    setExpandedLocation(!expandedLocation);
  };

  const toggleGroundFieldsExpansion = () => {
    setExpandedGroundFields(!expandedGroundFields);
  };

  const toggleBasicInfoExpansion = () => {
    setExpandedBasicInfo(!expandedBasicInfo);
  };

  const toggleImagesExpansion = () => {
    setExpandedImages(!expandedImages);
  };

  // Image handling functions
  const validateImage = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 2MB';
    }
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      return 'Only JPEG, PNG and GIF files are allowed';
    }
    return null;
  };

  const handleImageUpload = (event, setFieldValue, values) => {
    const files = Array.from(event.target.files);
    setUploadError('');

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const error = validateImage(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setUploadError(errors.join(', '));
      return;
    }

    // Create new image objects
    const newImages = validFiles.map((file, index) => ({
      id: `temp-${Date.now()}-${index}`,
      image: URL.createObjectURL(file),
      file: file, // Store the actual file for upload
      is_primary: values.images.length === 0 && index === 0 // First image becomes primary if no images exist
    }));

    setFieldValue('images', [...values.images, ...newImages]);
  };

  const handleSetPrimary = (imageIndex, setFieldValue, values) => {
    // Update local state for immediate UI feedback
    const updatedImages = values.images.map((image, index) => ({
      ...image,
      is_primary: index === imageIndex
    }));
    setFieldValue('images', updatedImages);

    toast.success('Primary image updated.');
  };

  const handleDeleteImage = (imageIndex, setFieldValue, values) => {
    const imageToDelete = values.images[imageIndex];

    const updatedImages = values.images.filter((_, index) => index !== imageIndex);

    // If we're deleting the primary image and there are other images, make the first one primary
    if (imageToDelete.is_primary && updatedImages.length > 0) {
      updatedImages[0].is_primary = true;
    }

    // If the image has an id, add it to images_to_delete
    if (imageToDelete.id) {
      const updatedImagesToDelete = [...(values.images_to_delete || []), imageToDelete.id];
      setFieldValue('images_to_delete', updatedImagesToDelete);
    }

    setFieldValue('images', updatedImages);
    toast.success('Image removed.');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e, setFieldValue, values) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const event = {target: {files}};
      handleImageUpload(event, setFieldValue, values);
    }
  };

  // Function to generate 1-hour slots between opening and closing time
  const generateHourlySlots = (openingTime, closingTime) => {
    if (!openingTime || !closingTime) return {};

    const slots = {};
    const opening = new Date(`2000-01-01T${openingTime}:00`);
    const closing = new Date(`2000-01-01T${closingTime}:00`);

    // Generate slots for each day
    DAYS_OF_WEEK.forEach(day => {
      slots[day] = [];
      let currentTime = new Date(opening);

      while (currentTime < closing) {
        const startTime = currentTime.toTimeString().slice(0, 5);
        currentTime.setHours(currentTime.getHours() + 1);
        const endTime = currentTime.toTimeString().slice(0, 5);

        // Only add slot if end time doesn't exceed closing time
        if (currentTime <= closing) {
          slots[day].push({
            start_time: startTime,
            end_time: endTime,
            price: 1000
          });
        }
      }
    });

    return slots;
  };

  // Function to track changes
  const trackChanges = (currentValues) => {
    if (!originalData) return {};

    const changes = {};

    // Track basic field changes
    const basicFields = ['name', 'contact_number', 'email', 'description', 'opening_time', 'closing_time', 'is_active'];
    basicFields.forEach(field => {
      const originalValue = originalData[field];
      const currentValue = currentValues[field];

      // Handle time format conversion for comparison
      if (field === 'opening_time' || field === 'closing_time') {
        const originalTime = originalValue ? originalValue.slice(0, 5) : '';
        if (originalTime !== currentValue) {
          changes[field] = currentValue;
        }
      } else if (originalValue !== currentValue) {
        changes[field] = currentValue;
      }
    });

    // Sports removed; ground sports derive from ground field sports

    // Track amenities changes
    const originalAmenities = originalData.amenities?.map(a => a.id) || [];
    const currentAmenities = currentValues.amenities || [];
    if (JSON.stringify(originalAmenities.sort()) !== JSON.stringify(currentAmenities.sort())) {
      changes.amenities = currentAmenities;
    }

    // Track location changes
    const originalLocation = {
      city: originalData.location?.city?.name || originalData.location?.city || '',
      address: originalData.location?.address || '',
      latitude: originalData.location?.latitude || null,
      longitude: originalData.location?.longitude || null,
      place_id: originalData.location?.place_id || null,
    };
    const currentLocation = currentValues.location;
    if (JSON.stringify(originalLocation) !== JSON.stringify(currentLocation)) {
      // Round coordinates to 8 decimal places to match backend validation
      const roundedLocation = {
        ...currentLocation,
        latitude: currentLocation.latitude ? parseFloat(Number(currentLocation.latitude).toFixed(8)) : null,
        longitude: currentLocation.longitude ? parseFloat(Number(currentLocation.longitude).toFixed(8)) : null,
      };
      changes.location = roundedLocation;
    }

    // Track main images changes
    const originalImages = originalData.images || [];
    const currentImages = currentValues.images || [];

    const imageChanges = {
      added: [],
      modified: [],
      deleted: currentValues.images_to_delete || []
    };

    // Find new images (with file property)
    currentImages.forEach(img => {
      if (img.file) {
        imageChanges.added.push({
          image: img.file,
          is_primary: img.is_primary
        });
      } else if (img.id) {
        // Check if existing image is modified (primary status changed)
        const originalImage = originalImages.find(oImg => oImg.id === img.id);
        if (originalImage && originalImage.is_primary !== img.is_primary) {
          imageChanges.modified.push({
            id: img.id,
            is_primary: img.is_primary
          });
        }
      }
    });

    // Always add images to changes if any of the arrays are non-empty
    if (imageChanges.added.length > 0 || imageChanges.modified.length > 0 || imageChanges.deleted.length > 0) {
      changes.images = imageChanges;
    }

    return changes;
  };

  // Approval actions
  const handleApprovalAction = async (action) => {
    if (!id) return;
    setApprovalAction(action);
    setShowApprovalModal(true);
  };

  const confirmApprovalAction = async () => {
    setApprovalLoading(true);
    try {
      await api.approveGround(id, {
        approval_status: approvalAction
      });
      setApprovalStatus(approvalAction);
      toast.success(`Ground ${APPROVAL_STATUS_LABEL[approvalAction]} successfully!`);
      navigate(routes.APPROVAL);
    } catch (error) {
      console.log(error);
    } finally {
      setApprovalLoading(false);
      setShowApprovalModal(false);
      setApprovalAction(null);
    }
  };

  // Helper function to get status display text
  const getStatusDisplayText = (status) => {
    return APPROVAL_STATUS_LABEL[status] || 'Pending';
  };

  // Helper function to check if action is allowed
  const isActionAllowed = (action) => {
    if (approvalLoading) return false;

    // If already approved, only allow reject (to change decision)
    if (approvalStatus === APPROVAL_STATUS.APPROVED) {
      return action === APPROVAL_STATUS.REJECTED;
    }

    // If already rejected, only allow approve (to change decision)
    if (approvalStatus === APPROVAL_STATUS.REJECTED) {
      return action === APPROVAL_STATUS.APPROVED;
    }

    // If pending, allow both actions
    return true;
  };

  if (!groundData && !fetchLoading) {
    return (<div className="ground-detail-page">
      <div className="error-container">
        <p>Ground not found</p>
      </div>
    </div>);
  }

  return (
    <div className="ground-detail-page detail-page">
      {(fetchLoading || approvalLoading) && <Loader/>}
      <div className="header">
        <h1>{approvalMode ? 'Review Ground for Approval' : 'Edit Ground'}</h1>
        <p
          className="subtitle">{approvalMode ? 'Review and approve or reject this ground listing.' : 'Update ground information and settings'}</p>
        {approvalMode && (
          <div className="approval-status-bar">
            <div className="approval-status-section">
              <span className={`approval-status ${approvalStatus}`}>
                {getStatusDisplayText(approvalStatus)}
              </span>
              {approvalStatus !== APPROVAL_STATUS.PENDING && (
                <div className="status-message">
                  {approvalStatus === APPROVAL_STATUS.APPROVED
                    ? 'This ground has been approved. You can still reject it if needed.'
                    : 'This ground has been rejected. You can still approve it if needed.'
                  }
                </div>
              )}
            </div>
            <div className="approval-actions">
              {approvalStatus !== APPROVAL_STATUS.APPROVED && (
                <button
                  className="approve-btn"
                  disabled={approvalLoading}
                  onClick={() => handleApprovalAction(APPROVAL_STATUS.APPROVED)}
                >
                  {approvalLoading ? 'Approving...' : 'Approve'}
                </button>
              )}
              {approvalStatus !== APPROVAL_STATUS.REJECTED && (
                <button
                  className="reject-btn"
                  disabled={approvalLoading}
                  onClick={() => handleApprovalAction(APPROVAL_STATUS.REJECTED)}
                >
                  {approvalLoading ? 'Rejecting...' : 'Reject'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={groundValidationSchema}
        onSubmit={handleSubmit}
        validateOnMount={false}
        validateOnChange={false}
        validateOnBlur={true}
        context={{isSubmitting: false}}
        enableReinitialize
      >
        {({values, errors, touched, setFieldValue}) => {
          return <Form className="custom-form">
            {/* Basic Information Section */}
            <div className="section-header">
              <div
                className="section-title-wrapper"
                onClick={toggleBasicInfoExpansion}
                style={{cursor: 'pointer'}}
              >
                <button
                  type="button"
                  className="expand-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleBasicInfoExpansion();
                  }}
                  aria-label={expandedBasicInfo ? 'Collapse basic information' : 'Expand basic information'}
                >
                    <span className={`expand-icon ${expandedBasicInfo ? 'expanded' : ''}`}>
                      {expandedBasicInfo ? <IoChevronUp/> : <IoChevronDown/>}
                    </span>
                </button>
                <h2>Basic Information</h2>
              </div>
            </div>

            {expandedBasicInfo && (<div className="basic-info-content">
                          <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">
                  Ground Name <span className="required">*</span>
                </label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                  placeholder="Enter ground name"
                  disabled={approvalMode}
                />
                <ErrorMessage name="name" component="div" className="error-message"/>
              </div>
              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <Field
                  type="email"
                  id="email"
                  name="email"
                  className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                  placeholder="Enter email address"
                  disabled={approvalMode}
                />
                <ErrorMessage name="email" component="div" className="error-message"/>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">
                Description <span className="required">*</span>
              </label>
              <Field
                as="textarea"
                id="description"
                name="description"
                className={`form-control ${touched.description && errors.description ? 'is-invalid' : ''}`}
                placeholder="Enter ground description"
                rows="4"
                disabled={approvalMode}
              />
              <ErrorMessage name="description" component="div" className="error-message"/>
            </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact_number">
                    Contact Number <span className="required">*</span>
                  </label>
                  <Field
                    type="tel"
                    id="contact_number"
                    name="contact_number"
                    className={`form-control ${touched.contact_number && errors.contact_number ? 'is-invalid' : ''}`}
                    placeholder="Enter contact number"
                    disabled={approvalMode}
                  />
                  <ErrorMessage name="contact_number" component="div" className="error-message"/>
                </div>
                <div className="form-group">
                  <label htmlFor="is_active">
                    Status
                  </label>
                  <Field
                    as="select"
                    id="is_active"
                    name="is_active"
                    className={`form-control ${touched.is_active && errors.is_active ? 'is-invalid' : ''}`}
                    disabled={approvalMode}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </Field>
                  <ErrorMessage name="is_active" component="div" className="error-message"/>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="opening_time">
                    Opening Time <span className="required">*</span>
                  </label>
                  <Field
                    type="time"
                    id="opening_time"
                    name="opening_time"
                    className={`form-control ${touched.opening_time && errors.opening_time ? 'is-invalid' : ''}`}
                    onChange={(e) => {
                      setFieldValue('opening_time', e.target.value);
                      // Trigger validation
                      setTimeout(() => {
                        const form = e.target.closest('form');
                        if (form) {
                          const event = new Event('change', {bubbles: true});
                          form.dispatchEvent(event);
                        }
                      }, 0);
                    }}
                    disabled={approvalMode}
                  />
                  <ErrorMessage name="opening_time" component="div" className="error-message"/>
                </div>
                <div className="form-group">
                  <label htmlFor="closing_time">
                    Closing Time <span className="required">*</span>
                  </label>
                  <Field
                    type="time"
                    id="closing_time"
                    name="closing_time"
                    className={`form-control ${touched.closing_time && errors.closing_time ? 'is-invalid' : ''}`}
                    onChange={(e) => {
                      setFieldValue('closing_time', e.target.value);
                      // Trigger validation
                      setTimeout(() => {
                        const form = e.target.closest('form');
                        if (form) {
                          const event = new Event('change', {bubbles: true});
                          form.dispatchEvent(event);
                        }
                      }, 0);
                    }}
                    disabled={approvalMode}
                  />
                  <ErrorMessage name="closing_time" component="div" className="error-message"/>
                </div>
              </div>

              {/* Show opening/closing time validation error */}
              {values.opening_time && values.closing_time && (
                (() => {
                  const openingTime = new Date(`2000-01-01T${values.opening_time}:00`);
                  const closingTime = new Date(`2000-01-01T${values.closing_time}:00`);
                  if (openingTime >= closingTime) {
                    return <div className="error-message time-validation-error">
                      {'Opening time must be less than closing time'}
                    </div>
                  }
                  return null;
                })()
              )}

              {/* Sports removed; ground sports derive from ground field sports */}

              {/* Amenities Section */}
              <div className="form-group">
                <label>
                  Amenities <span className="required">*</span>
                </label>
                {amenitiesLoading ? (
                  <div style={{color: '#888', padding: '8px 0'}}>Loading amenities...</div>
                ) : amenitiesError ? (
                  <div style={{color: '#d32f2f', padding: '8px 0'}}>Failed to load amenities</div>
                ) : (
                  <MultiSelect
                    options={amenities.map(a => ({label: a.name, value: a.id}))}
                    value={amenities.filter(a => (values.amenities || []).includes(a.id)).map(a => ({
                      label: a.name,
                      value: a.id
                    }))}
                    onChange={selected => setFieldValue('amenities', selected.map(opt => opt.value))}
                    placeholder="Select amenities..."
                    disabled={approvalMode}
                  />
                )}
                <ErrorMessage name="amenities" component="div" className="error-message"/>
              </div>
            </div>)}

            {/* Images Section */}
            <div className="section-header">
              <div
                className="section-title-wrapper"
                onClick={toggleImagesExpansion}
                style={{cursor: 'pointer'}}>
                <button
                  type="button"
                  className="expand-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleImagesExpansion();
                  }}
                  aria-label={expandedImages ? 'Collapse images' : 'Expand images'}
                >
                <span className={`expand-icon ${expandedImages ? 'expanded' : ''}`}>
                  {expandedImages ? <IoChevronUp/> : <IoChevronDown/>}
                </span>
                </button>
                <h2>Ground Images</h2>
              </div>
            </div>
            {expandedImages && (
              <div className="images-content">
                <MultiImages
                  images={values.images}
                  onSetPrimary={approvalMode ? undefined : (index => handleSetPrimary(index, setFieldValue, values))}
                  onDelete={approvalMode ? undefined : (index => handleDeleteImage(index, setFieldValue, values))}
                  onUpload={approvalMode ? undefined : (e => handleImageUpload(e, setFieldValue, values))}
                  loading={loading}
                  errors={errors.images}
                  readOnly={approvalMode}
                  title="Ground Images"
                />
                {uploadError && (<div className="upload-error">{uploadError}</div>)}
              </div>
            )}

            {/* Location Section */}
            <div className="section-header">
              <div
                className="section-title-wrapper"
                onClick={toggleLocationExpansion}
                style={{cursor: 'pointer'}}
              >
                <button
                  type="button"
                  className="expand-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLocationExpansion();
                  }}
                  aria-label={expandedLocation ? 'Collapse location' : 'Expand location'}
                >
                    <span className={`expand-icon ${expandedLocation ? 'expanded' : ''}`}>
                      {expandedLocation ? <IoChevronUp/> : <IoChevronDown/>}
                    </span>
                </button>
                <h2>Location Information</h2>
              </div>
            </div>

            {expandedLocation && (<div className="location-content">
              <div className="form-group">
                <label>
                  Location <span className="required">*</span>
                </label>
                <GoogleLocationPicker
                  value={values.location}
                  onChange={(location) => {
                    setFieldValue('location', location);
                  }}
                  disabled={approvalMode}
                  apiKey={GOOGLE_MAPS_API_KEY}
                />
                {values.location?.address && (
                  <div className="location-display" style={{marginTop: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px'}}>
                    <div style={{fontWeight: '500', marginBottom: '4px'}}>Selected Location:</div>
                    <div style={{color: '#666', fontSize: '14px'}}>
                      <div><strong>Address:</strong> {values.location.address}</div>
                      {values.location.city && (
                        <div style={{marginTop: '4px'}}><strong>City:</strong> {values.location.city}</div>
                      )}
                    </div>
                  </div>
                )}
                <ErrorMessage name="location.address" component="div" className="error-message"/>
                <ErrorMessage name="location.city" component="div" className="error-message"/>
              </div>
              
              {/* Hidden fields for address and city to maintain form validation */}
              <Field
                type="hidden"
                name="location.address"
                value={values.location?.address || ''}
              />
              <Field
                type="hidden"
                name="location.city"
                value={values.location?.city || ''}
              />
            </div>)}

            {approvalMode && <>
              {/* Ground Fields Section */}
              <div className="section-header">
                <div
                  className="section-title-wrapper"
                  onClick={toggleGroundFieldsExpansion}
                  style={{cursor: 'pointer'}}
                >
                  <button
                    type="button"
                    className="expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroundFieldsExpansion();
                    }}
                    aria-label={expandedGroundFields ? 'Collapse ground fields' : 'Expand ground fields'}
                  >
                    <span className={`expand-icon ${expandedGroundFields ? 'expanded' : ''}`}>
                      {expandedGroundFields ? <IoChevronUp/> : <IoChevronDown/>}
                    </span>
                  </button>
                  <h2>Ground Fields</h2>
                </div>
              </div>
              {expandedGroundFields && (<div className="ground-fields-content">
                <FieldArray name="ground_fields">
                  {({push, remove}) => (<div>
                    {values.ground_fields.map((field, fieldIndex) => (<div key={fieldIndex} className="field-section">
                      <div className="field-header">
                        <div
                          className="field-title-section"
                          onClick={() => toggleFieldExpansion(fieldIndex)}
                          style={{cursor: 'pointer'}}
                        >
                          <button
                            type="button"
                            className="expand-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFieldExpansion(fieldIndex);
                            }}
                            aria-label={expandedFields.has(fieldIndex) ? 'Collapse field' : 'Expand field'}
                          >
                                <span className={`expand-icon ${expandedFields.has(fieldIndex) ? 'expanded' : ''}`}>
                                  {expandedFields.has(fieldIndex) ? <IoChevronUp/> : <IoChevronDown/>}
                                </span>
                          </button>
                          <h3>{field.name ? field.name : `Field ${fieldIndex + 1}`}</h3>
                        </div>
                      </div>

                      {expandedFields.has(fieldIndex) && (<div className="field-content">
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`ground_fields.${fieldIndex}.name`}>
                              Field Name <span className="required">*</span>
                            </label>
                            <Field
                              type="text"
                              id={`ground_fields.${fieldIndex}.name`}
                              name={`ground_fields.${fieldIndex}.name`}
                              className={`form-control ${touched.ground_fields?.[fieldIndex]?.name && errors.ground_fields?.[fieldIndex]?.name ? 'is-invalid' : ''}`}
                              placeholder="Enter field name"
                              disabled={approvalMode}
                            />
                            <ErrorMessage name={`ground_fields.${fieldIndex}.name`} component="div"
                                          className="error-message"/>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>
                            Field Sports <span className="required">*</span>
                          </label>
                          {sportsLoading ? (
                            <div style={{color: '#888', padding: '8px 0'}}>Loading sports...</div>
                          ) : sportsError ? (
                            <div style={{color: '#d32f2f', padding: '8px 0'}}>Failed to load sports</div>
                          ) : (
                            <MultiSelect
                              options={sports.map(s => ({label: s.name, value: s.id}))}
                              value={sports.filter(s => (values.ground_fields[fieldIndex].sports || []).includes(s.id)).map(s => ({
                                label: s.name,
                                value: s.id
                              }))}
                              onChange={selected => setFieldValue(`ground_fields.${fieldIndex}.sports`, selected.map(opt => opt.value))}
                              placeholder="Select sports..."
                              disabled={approvalMode}
                            />
                          )}
                          <ErrorMessage name={`ground_fields.${fieldIndex}.sports`} component="div"
                                        className="error-message"/>
                        </div>

                        {/* Field Images Section */}
                        <div className="form-group">
                          <label>Field Images</label>
                          <MultiImages
                            images={values.ground_fields[fieldIndex].images || []}
                            onSetPrimary={approvalMode ? undefined : (imgIdx => {
                              const updated = [...values.ground_fields];
                              updated[fieldIndex].images = updated[fieldIndex].images.map((img, i) => ({
                                ...img,
                                is_primary: i === imgIdx
                              }));
                              setFieldValue('ground_fields', updated);
                            })}
                            onDelete={approvalMode ? undefined : (imgIdx => {
                              const updated = [...values.ground_fields];
                              const imageToDelete = updated[fieldIndex].images[imgIdx];
                              updated[fieldIndex].images = updated[fieldIndex].images.filter((_, i) => i !== imgIdx);
                              // If the image has an id, track it for deletion
                              if (imageToDelete.id) {
                                updated[fieldIndex].images_to_delete = [
                                  ...(updated[fieldIndex].images_to_delete || []),
                                  imageToDelete.id
                                ];
                              }
                              setFieldValue('ground_fields', updated);
                            })}
                            onUpload={approvalMode ? undefined : (e => {
                              const files = Array.from(e.target.files);
                              const validFiles = files.map((file, idx) => ({
                                id: `temp-${Date.now()}-${idx}`,
                                image: URL.createObjectURL(file),
                                file,
                                is_primary: (values.ground_fields[fieldIndex].images?.length || 0) === 0 && idx === 0
                              }));
                              const updated = [...values.ground_fields];
                              updated[fieldIndex].images = [
                                ...(updated[fieldIndex].images || []),
                                ...validFiles
                              ];
                              setFieldValue('ground_fields', updated);
                            })}
                            loading={loading}
                            errors={errors.ground_fields?.[fieldIndex]?.images}
                            readOnly={approvalMode}
                            title={`Field Images - ${values.ground_fields[fieldIndex].name || `Field ${fieldIndex + 1}`}`}
                          />
                        </div>

                        {/* Fields Section */}
                        <div className="slots-section">
                          <div
                            className="slots-header"
                            onClick={() => toggleSlotsExpansion(fieldIndex)}
                            style={{cursor: 'pointer'}}
                          >
                            <div className="slots-title-section">
                              <button
                                type="button"
                                className="expand-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSlotsExpansion(fieldIndex);
                                }}
                                aria-label={expandedSlots.has(fieldIndex) ? 'Collapse slots' : 'Expand slots'}
                              >
                                      <span
                                        className={`expand-icon ${expandedSlots.has(fieldIndex) ? 'expanded' : ''}`}>
                                        {expandedSlots.has(fieldIndex) ? <IoChevronUp/> : <IoChevronDown/>}
                                      </span>
                              </button>
                              <h4>Time Slots</h4>
                            </div>
                          </div>

                          {expandedSlots.has(fieldIndex) && (<div className="slots-content">
                            {DAYS_OF_WEEK.map((day) => (<div key={day} className="day-slots">
                              <div
                                className="day-header"
                                onClick={() => toggleDayExpansion(day)}
                                style={{cursor: 'pointer'}}
                              >
                                <div className="day-title-section">
                                  <button
                                    type="button"
                                    className="expand-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDayExpansion(day);
                                    }}
                                    aria-label={expandedDays.has(day) ? 'Collapse day' : 'Expand day'}
                                  >
                                                <span
                                                  className={`expand-icon ${expandedDays.has(day) ? 'expanded' : ''}`}>
                                                  {expandedDays.has(day) ? <IoChevronUp/> : <IoChevronDown/>}
                                                </span>
                                  </button>
                                  <h5>{day.charAt(0).toUpperCase() + day.slice(1)}</h5>
                                  <span className="slot-count">
                                                ({values.ground_fields[fieldIndex].field_slots?.[day]?.length || 0} slots)
                                              </span>
                                </div>
                              </div>

                              {expandedDays.has(day) && (<div className="day-slots-content">
                                <FieldArray name={`ground_fields.${fieldIndex}.field_slots.${day}`}>
                                  {({push: pushSlot, remove: removeSlot}) => (<div>
                                    <div className="slots-table-container">
                                      <table className="slots-table">
                                        <thead>
                                        <tr>
                                          <th>Start Time</th>
                                          <th>End Time</th>
                                          <th>Price (PKR)</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {values.ground_fields[fieldIndex].field_slots?.[day]?.map((slot, slotIndex) => (
                                          <tr key={slotIndex} className="slot-row">
                                            <td>
                                              <Field
                                                type="time"
                                                name={`ground_fields.${fieldIndex}.field_slots.${day}.${slotIndex}.start_time`}
                                                className="form-control"
                                                disabled={approvalMode}
                                              />
                                              <ErrorMessage
                                                name={`ground_fields.${fieldIndex}.field_slots.${day}.${slotIndex}.start_time`}
                                                component="div"
                                                className="error-message"
                                              />
                                            </td>
                                            <td>
                                              <Field
                                                type="time"
                                                name={`ground_fields.${fieldIndex}.field_slots.${day}.${slotIndex}.end_time`}
                                                className="form-control"
                                                disabled={approvalMode}
                                              />
                                              <ErrorMessage
                                                name={`ground_fields.${fieldIndex}.field_slots.${day}.${slotIndex}.end_time`}
                                                component="div"
                                                className="error-message"
                                              />
                                            </td>
                                            <td>
                                              <Field
                                                type="number"
                                                name={`ground_fields.${fieldIndex}.field_slots.${day}.${slotIndex}.price`}
                                                className="form-control"
                                                placeholder="Enter price"
                                                min="0"
                                                disabled={approvalMode}
                                              />
                                              <ErrorMessage
                                                name={`ground_fields.${fieldIndex}.field_slots.${day}.${slotIndex}.price`}
                                                component="div"
                                                className="error-message"
                                              />
                                            </td>
                                          </tr>))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>)}
                                </FieldArray>
                              </div>)}
                            </div>))}
                          </div>)}
                        </div>
                      </div>)}
                    </div>))}
                    {!approvalMode && <button
                      type="button"
                      className="add-field-btn"
                      onClick={() => {
                        const newFieldIndex = values.ground_fields.length;
                        // Generate slots based on current opening/closing times
                        const fieldSlots = values.opening_time && values.closing_time
                          ? generateHourlySlots(values.opening_time, values.closing_time)
                          : DAYS_OF_WEEK.reduce((acc, day) => {
                            acc[day] = [{start_time: '', end_time: '', price: 1000}];
                            return acc;
                          }, {});

                        push({
                          name: '',
                          sports: [],
                          images: [],
                          field_slots: fieldSlots
                        });
                        // Auto-expand the new field
                        setExpandedFields(prev => new Set([...prev, newFieldIndex]));
                      }}
                      disabled={approvalMode}
                    >
                      <IoAdd size={24}/>
                      Add Field
                    </button>}
                  </div>)}
                </FieldArray>
              </div>)}
            </>}

            {groundData && (
              <div className="info-section">
                <div className="info-item">
                  <span className="info-label">Ground ID:</span>
                  <span className="info-value">{groundData.id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Created:</span>
                  <span className="info-value">
                    {new Date(groundData.created).toLocaleDateString()}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Modified:</span>
                  <span className="info-value">
                    {new Date(groundData.modified).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}

            {/* Actions Section */}
            {!approvalMode && (
              <div className="actions-section">
                <button
                  className="submit-btn primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <ComLoader/> : "Save Changes"}
                </button>

                <button
                  className="submit-btn secondary"
                  type="button"
                  onClick={handleCancel}
                  disabled={loading || deleteLoading}
                >
                  Cancel
                </button>

                <button
                  className="submit-btn danger"
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={loading || deleteLoading}
                >
                  Delete Ground
                </button>
              </div>
            )}
          </Form>
        }}
      </Formik>

      <ConfirmModal
        show={showDeleteModal}
        title="Delete Ground"
        message="Are you sure you want to delete this ground? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        loading={deleteLoading}
        variant="danger"
      />

      <ConfirmModal
        show={showApprovalModal}
        title={approvalAction === APPROVAL_STATUS.APPROVED ? "Approve Ground" : "Reject Ground"}
        message={approvalAction === APPROVAL_STATUS.APPROVED 
          ? "Are you sure you want to approve this ground?"
          : "Are you sure you want to reject this ground?"
        }
        confirmText={approvalAction === APPROVAL_STATUS.APPROVED ? "Approve" : "Reject"}
        cancelText="Cancel"
        onConfirm={confirmApprovalAction}
        onCancel={() => {
          setShowApprovalModal(false);
          setApprovalAction(null);
        }}
        loading={approvalLoading}
        variant={approvalAction === APPROVAL_STATUS.APPROVED ? "primary" : "danger"}
      />
    </div>);
}

export default GroundDetail; 